// Venue-agnostic REST transport, lifted out of KalshiClient so a second venue reuses its retry/pacing/signing logic.

import { callBridge } from '../kalshi/bridge';
import { logError, logNet, logOdd } from '../core/log';
import { journal } from '../core/journal';

export class VenueAPIError extends Error {
  /** `order`: what a read-back found, when the error came out of one (a cancel that found the order already closed). */
  constructor(public status: number, public text: string, public order?: unknown) {
    // An edge (Cloudflare) error page is HTML; its doctype is worthless in the log, so it's summarized instead.
    super(`HTTP ${status}: ${/^\s*<(!doctype|html)/i.test(text) ? 'HTML error page from the edge (rate limited or blocked)' : text.slice(0, 200)}`);
    this.name = 'VenueAPIError';
  }
}

/** Raised when a POST outcome is genuinely unknown (transport died mid-flight). */
export class AmbiguousPlacement extends Error {}

export interface RawResponse {
  status: number;
  text: string;
  date: string;
  retryAfter: string;
}

// Reads-only token bucket (tokens/sec, FIFO queue): orders and cancels are never paced, queued, or held here.
class TokenBucket {
  private tokens: number;
  private ts = performance.now();
  private tail: Promise<void> = Promise.resolve();

  constructor(private rate: number, private burst: number) {
    this.tokens = burst;
  }

  acquire(cost: number): Promise<void> {
    // A cost above capacity could never be satisfied and would wait forever.
    const want = Math.min(cost, this.burst);
    const run = this.tail.then(() => this.take(want));
    this.tail = run.catch(() => undefined);
    return run;
  }

  private async take(cost: number): Promise<void> {
    for (;;) {
      const now = performance.now();
      this.tokens = Math.min(this.burst, this.tokens + ((now - this.ts) / 1000) * this.rate);
      this.ts = now;
      if (this.tokens >= cost) {
        this.tokens -= cost;
        return;
      }
      await sleep(((cost - this.tokens) / this.rate) * 1000);
    }
  }
}

export const sleep = (ms: number): Promise<void> => new Promise<void>((r) => setTimeout(r, ms));

export interface ClientOptions {
  defaultTimeoutMs?: number;
  maxRetries?: number;
}

/** An array value is sent as a repeated key (`categories=a&categories=b`). */
export type QueryParams = Record<string, string | number | string[] | undefined>;

export interface RequestOptions {
  params?: QueryParams;
  json?: unknown;
  timeoutMs?: number;
  /** Statuses that are normal for this call, so the log does not flag them. */
  expected?: number[];
  expectedNote?: string;
  /** 'public' sends the request to the venue's unauthenticated host, if it has one. */
  base?: 'public';
  /** Journal tag: the arb order and leg this request is for. */
  tag?: string;
}

export interface TransportConfig extends ClientOptions {
  /** The key in the extension that signs these requests. */
  venue: string;
  /** Log source column; Kalshi keeps 'rest' so its log lines read as before. */
  source?: string;
  /** [tokens per second, burst] for GETs. Writes are never paced. */
  read: [number, number];
  /** Kept for callers that still pass it; ignored. */
  write?: [number, number];
  /** Token cost of one request; defaults to 1. */
  cost?: (method: string, path: string) => number;
}

/** Compact query string, for log lines only — the real one is built in the worker. */
function qs(params?: QueryParams): string {
  if (!params) return '';
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${String(v).slice(0, 24)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

// Local clock versus the venue's, from response Date headers; measured and exposed for a UI banner.
let clockSkewMs = 0;
const skewListeners = new Set<(skewMs: number) => void>();
/** Positive: this machine is behind the venue. */
export function clockSkew(): number {
  return clockSkewMs;
}
export function onClockSkew(fn: (skewMs: number) => void): () => void {
  skewListeners.add(fn);
  return () => { skewListeners.delete(fn); };
}
const SKEW_WARN_MS = 15_000;

export class RestTransport {
  readonly venue: string;
  protected readonly maxRetries: number;
  protected readonly defaultTimeoutMs: number;
  private readonly source: string;
  private readonly readBucket: TokenBucket;
  private readonly cost: (method: string, path: string) => number;
  private clockSyncInFlight: Promise<void> | null = null;
  /** After a 429 on a read, reads wait until here; writes never look at it. */
  private readCooldownUntil = 0;

  constructor(cfg: TransportConfig) {
    this.venue = cfg.venue;
    this.source = cfg.source ?? cfg.venue;
    this.maxRetries = cfg.maxRetries ?? 3;
    this.defaultTimeoutMs = cfg.defaultTimeoutMs ?? 10_000;
    this.readBucket = new TokenBucket(cfg.read[0], cfg.read[1]);
    this.cost = cfg.cost ?? (() => 1);
  }

  // POST is not idempotent, so it only auto-retries on 429 (rejected before processing); other ambiguous
  // outcomes surface to the caller to resolve by coid. `path` must be query-free — that's what gets signed.
  async request(method: string, path: string, opts: RequestOptions = {}): Promise<RawResponse> {
    const idempotent = method.toUpperCase() !== 'POST';
    const isRead = method.toUpperCase() === 'GET';
    const cost = this.cost(method, path);
    const timeoutMs = opts.timeoutMs ?? this.defaultTimeoutMs;
    let resynced = false;

    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      if (isRead) {
        await this.readBucket.acquire(cost);
        // A 429 on one read pauses all reads to avoid a stampede; orders and cancels skip this entirely.
        const wait = this.readCooldownUntil - Date.now();
        if (wait > 0) await sleep(wait);
      }
      // Each attempt is a fresh round trip, so it gets a fresh signature (a re-used timestamp fails on retry).
      const t0 = Date.now();
      const resp = await callBridge({
        type: 'REST',
        venue: this.venue,
        base: opts.base,
        method: method.toUpperCase(),
        path,
        params: opts.params,
        body: opts.json === undefined ? null : JSON.stringify(opts.json),
        timeoutMs,
      }, timeoutMs + 5_000);
      const ms = Date.now() - t0;
      const label = `${method.toUpperCase()} ${path}${qs(opts.params)}`;

      if (!isRead) {
        journal({
          venue: this.venue, kind: 'wire', tag: opts.tag, bad: !resp.ok || resp.status === undefined || Number(resp.status) >= 300,
          msg: `${label} → ${resp.ok && resp.status !== undefined ? resp.status : `bridge: ${String(resp.error || 'failure')}`} (${ms}ms)${attempt ? ` attempt ${attempt + 1}` : ''}`,
          data: { method: method.toUpperCase(), path, params: opts.params, body: opts.json, status: resp.status ?? null, error: resp.error ?? null, response: resp.text ?? null, ms },
        });
      }
      if (!resp.ok || resp.status === undefined) {
        const err = String(resp.error || 'bridge failure');
        logError(this.source, `${label} — ${err} (${ms}ms)`);
        if (!idempotent || attempt >= this.maxRetries) throw new AmbiguousPlacement(err);
        await this.backoff(attempt);
        continue;
      }
      // 2xx (including a placed order's 201) logs as success; `expected` carves out routine non-2xx statuses.
      const code = Number(resp.status);
      const ok2xx = code >= 200 && code < 300;
      const expected = opts.expected?.includes(code) ?? false;
      const note = expected && opts.expectedNote ? ` · ${opts.expectedNote}` : '';
      // Carry the body on anything unusual, so a surprising status isn't just a bare number.
      const body = ok2xx ? '' : ` ${String(resp.text ?? '').replace(/\s+/g, ' ').slice(0, 140)}`;
      (ok2xx || expected ? logNet : logOdd)(this.source, `${label} → ${resp.status} (${ms}ms)${note}${body}`);
      const r: RawResponse = {
        status: Number(resp.status),
        text: String(resp.text ?? ''),
        date: String(resp.date ?? ''),
        retryAfter: String(resp.retryAfter ?? ''),
      };
      this.noteSkew(r.date, ms);
      if (r.status === 0) {
        // Transport error inside the service worker.
        if (!idempotent || attempt >= this.maxRetries) throw new AmbiguousPlacement(String(resp.error || 'transport error'));
        await this.backoff(attempt);
        continue;
      }
      if (r.status === 401 && !resynced) {
        // 401 is an authoritative "not processed", so even POSTs retry safely once, after a clock resync.
        resynced = true;
        await this.resyncClock();
        continue;
      }
      if (r.status === 429) {
        const hinted = Number(r.retryAfter);
        let waitMs: number;
        if (isRead) {
          // Retry-After is honoured but never trusted below a second — the edge can send 0.
          waitMs = Math.min(30_000, Math.max(Number.isFinite(hinted) ? hinted * 1000 : 0, 1_000 * 2 ** attempt + Math.random() * 500));
          // Not stacked, so a burst of reads each seeing a 429 doesn't push the hold further out each time.
          this.readCooldownUntil = Math.max(this.readCooldownUntil, Math.min(Date.now() + waitMs, Date.now() + 8_000));
        } else {
          // A write was refused before processing: use only the venue's own hint, no local schedule on top.
          waitMs = Math.min(5_000, Number.isFinite(hinted) && hinted > 0 ? hinted * 1000 : 250);
        }
        if (attempt < this.maxRetries) {
          await sleep(waitMs);
          continue;
        }
      }
      if (r.status >= 500 && idempotent && attempt < this.maxRetries) {
        await this.backoff(attempt, r);
        continue;
      }
      return r;
    }
    throw new VenueAPIError(0, 'request loop exited unexpectedly');
  }

  protected async backoff(attempt: number, resp?: RawResponse): Promise<void> {
    if (resp?.retryAfter) {
      const secs = Number(resp.retryAfter);
      if (Number.isFinite(secs)) {
        await sleep(Math.min(secs, 30) * 1000);
        return;
      }
    }
    await sleep(Math.min(250 * 2 ** attempt + Math.random() * 250, 8_000));
  }

  private lastSkewNoted = 0;

  /** Date headers carry whole seconds, so anything under a few seconds is noise. */
  private noteSkew(dateHeader: string, roundTripMs: number): void {
    const server = Date.parse(dateHeader);
    if (!Number.isFinite(server)) return;
    const skew = server - (Date.now() - roundTripMs / 2);
    const was = clockSkewMs;
    clockSkewMs = Math.abs(skew) < 3_000 ? 0 : skew;
    if ((Math.abs(was) >= SKEW_WARN_MS) !== (Math.abs(clockSkewMs) >= SKEW_WARN_MS) || Math.abs(clockSkewMs - was) > 10_000) {
      for (const fn of skewListeners) { try { fn(clockSkewMs); } catch { /* ignore */ } }
    }
    // Skew is handled, not just announced: a resync keeps the worker's clock reference fresh while it persists.
    if (Math.abs(clockSkewMs) >= SKEW_WARN_MS && Date.now() - this.lastSkewNoted > 60_000) {
      this.lastSkewNoted = Date.now();
      void this.resyncClock();
    }
  }

  async resyncClock(): Promise<void> {
    if (this.clockSyncInFlight) return this.clockSyncInFlight;
    this.clockSyncInFlight = (async () => {
      try {
        const r = await callBridge({ type: 'RESYNC_CLOCK', venue: this.venue }, 8_000);
        if (r.ok && r.synced) {
          console.info(`[${this.venue}] clock synced`, new Date(Number(r.serverMs)).toISOString());
        }
      } finally {
        this.clockSyncInFlight = null;
      }
    })();
    return this.clockSyncInFlight;
  }

  protected async getJson<T = any>(path: string, opts: Omit<RequestOptions, 'json'> = {}): Promise<T> {
    const r = await this.request('GET', path, opts);
    if (r.status !== 200) throw new VenueAPIError(r.status, r.text);
    return JSON.parse(r.text || '{}') as T;
  }
}
